'use client';

import type { Option } from 'effect';
import { preload as preloadResource } from 'react-dom';

import { useImageFallbackFlow } from '../useImageFallbackFlow';
import {
  shouldBypassImageOptimization,
  type ImageBypassRule,
} from './shouldBypassImageOptimization';

type UseVsImageOptions<S> = {
  src: S;
  fallback: Option.Option<S>;
  preload?: boolean;
  unoptimized?: boolean;
  bypass?: ImageBypassRule[];
};

/**
 * The reusable, framework-free behaviour of a smart image — fallback flow,
 * optional preload hint, and the optimizer-bypass decision — minus the render.
 * The host app renders the actual element (e.g. `next/image`) from the returned
 * `current` / `stage` / `imgRef` / `unoptimized` and wires `handleError`.
 */
export function useVsImage<S>({
  src,
  fallback,
  preload = false,
  unoptimized = false,
  bypass = [],
}: UseVsImageOptions<S>) {
  if (preload && typeof src === 'string') {
    preloadResource(src, { as: 'image' });
  }

  const { stage, current, imgRef, isError, handleError } = useImageFallbackFlow<S>({
    primary: src,
    fallback,
  });

  const resolvedUnoptimized = shouldBypassImageOptimization(current, bypass) || unoptimized;

  return { stage, current, imgRef, isError, handleError, unoptimized: resolvedUnoptimized };
}
